import { Backdrop, CircularProgress } from '@mui/material';
import React, { createContext, useContext, useState, PropsWithChildren, useEffect } from 'react';

interface LoaderContextState {
    isLoading: boolean;
    setIsLoading: (isLoading: boolean) => void;
}

const defaultLoaderContextState: LoaderContextState = {
    isLoading: false,
    setIsLoading: () => { },
};

const LoaderContext = createContext(
    defaultLoaderContextState
);

const LoaderBackdrop: React.FC = () => {
    const { isLoading } = useContext(LoaderContext);
    return (
        <Backdrop
            open={isLoading}
            sx={{
                color: '#fff',
                zIndex: (theme) => theme.zIndex.drawer + 1,
            }}
        >
            <CircularProgress color="inherit" />
        </Backdrop>
    );
};

const useLoader = (loading?: boolean) => {
    const { setIsLoading } = useContext(LoaderContext);

    useEffect(() => {
        if (loading !== undefined) {
            setIsLoading(loading);
        }
        return () => {
            if (loading !== undefined) {
                setIsLoading(false);
            }
        };
    }, [loading, setIsLoading]);

    const showLoader = () => setIsLoading(true);
    const hideLoader = () => setIsLoading(false);

    return {
        showLoader,
        hideLoader,
    };
};

const LoaderProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const [isLoading, setIsLoading] = useState(defaultLoaderContextState.isLoading);
    return (
        <LoaderContext.Provider value={{ isLoading, setIsLoading }}>
            <LoaderBackdrop />
            {children}
        </LoaderContext.Provider>
    );
};

export { LoaderProvider, useLoader };
